import { createModel } from 'js-crc'
import { areByteArraysEqual } from 'utils/common'
import { PacketId } from 'utils/common-types'
import { CSTORE_SIZE, CSTORE_MAGIC_BYTES } from 'cstore/cstore'

// This uses the FIRMWARE_UPDATE packet ID which should really have been called MANAGE_FLASH.
//
// NOTE: should be kept in sync with FWUpdateState_t in FW code
export enum FlashManagementCommand {
  ERASE_BANK = 1,
  PROGRAM_MEMORY,
  END_OF_UPDATE,
  READ_FLASH,
  GET_FLASH_INFO,
  ERASE_FLASH_CONFIG,
  WRITE_FLASH_CONFIG,
  RELOAD_CONFIG
}

export enum FlashAction {
  FlashExecutable,
  FlashConfig,
  FlashExeAndConfig,
  BackupFirmware
}

// Keep in sync with changes to above
export const isFlashOp = (action: FlashAction) => action !== FlashAction.BackupFirmware

export interface IFlashMgmtRequest {
  command: FlashManagementCommand
  byteOffset?: number
  chunk?: Uint32Array
  chunkSize?: number // Size in bytes if no chunk supplied
}

// Callback for reporting progress
export type ProgressCb = (progress: number) => void

export class PermanentFailure extends Error {
  constructor(m: string) {
    super(m)

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, PermanentFailure.prototype)
  }
}

// Serialize a request packet for managing flash
// Note: `byteOffset` must be supplied if this update command uses `chunk`.
export const serializeFlashMgmtRequest = (request: IFlashMgmtRequest): DataView => {
  const dv = new DataView(new ArrayBuffer(64))
  dv.setUint8(0, PacketId.FIRMWARE_UPDATE)
  dv.setUint8(1, request.command)
  if (isDefined(request.byteOffset)) {
    dv.setUint32(8, request.byteOffset, true)
  }
  let chunkSizeBytes = 0
  if (isDefined(request.chunkSize)) chunkSizeBytes = request.chunkSize
  if (isDefined(request.chunk)) chunkSizeBytes = request.chunk.length * 4

  if (isDefined(request.byteOffset)) {
    dv.setUint32(4, chunkSizeBytes, true)
    if (isDefined(request.chunk)) {
      for (const [i, u32] of request.chunk.entries()) {
        dv.setUint32(12 + i * 4, u32, true)
      }
    }
  }
  return dv
}

export const fileToBytes = (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = (e) => {
      if (
        e.target === null ||
        !isDefined(e.target.result) ||
        e.target.readyState !== FileReader.DONE ||
        typeof e.target.result !== 'object'
      ) {
        reject()
        return
      }

      const buffer = e.target.result
      resolve(new Uint8Array(buffer))
    }
    reader.readAsArrayBuffer(file)
  })
}

// Use relaxedCheck=true for cstore bin files (non-cfw)
export const getCStoreRegionOffset = (fileBytes: Uint8Array, relaxedCheck = false): number => {
  let offset = fileBytes.length - CSTORE_SIZE
  if (offset % 8 !== 0 || offset < 0 || (!relaxedCheck && offset === 0)) {
    throw new PermanentFailure('cstore magic bytes not found')
  }

  const sub = fileBytes.subarray(offset, offset + 8)
  if (areByteArraysEqual(sub, CSTORE_MAGIC_BYTES)) {
    return offset
  } else {
    throw new PermanentFailure('cstore magic bytes not found')
  }
}

export const CRC_INFO_START: Uint8Array = new TextEncoder().encode('CRC_INFO_START->')
export const CRC_INFO_END: Uint8Array = new TextEncoder().encode('<---CRC_INFO_END')

export type CrcData = {
  imageCrc: number // uint32_t
  imageStart: number // uint32_t; we don't care about the start location in device address space
  imageSize: number // uint32_t
}

export const CRC_DATA_SIZE =
  CRC_INFO_START.length + CRC_INFO_END.length + 3 /* fieldcount above */ * 4 /* sizeof(uint32_t) */

// Call this on a firmware image which may or may not have CRC data.
// Compare against add_crc.py from Salient-Platform commit ab9d782
// Note: this assumes the CRC_INFO_START is on 4-byte alignment -- probably a
// good assumption...
export const getCrcData = (fileBytes: Uint8Array): CrcData | undefined => {
  if (fileBytes.length % 4 !== 0) return
  for (let offset = 0; offset + CRC_DATA_SIZE <= fileBytes.length; offset += 4) {
    const sub = fileBytes.subarray(offset, offset + CRC_INFO_START.length)
    if (!areByteArraysEqual(sub, CRC_INFO_START)) continue
    const sub2 = fileBytes.subarray(
      offset + CRC_DATA_SIZE - CRC_INFO_END.length,
      offset + CRC_DATA_SIZE
    )
    if (areByteArraysEqual(sub2, CRC_INFO_END)) {
      // Create a data view that starts at the first CrcData field
      const dv = new DataView(
        fileBytes.buffer,
        fileBytes.byteOffset + offset + CRC_INFO_START.length
      )
      return {
        imageCrc: dv.getUint32(0, true),
        imageStart: dv.getUint32(4, true),
        imageSize: dv.getUint32(8, true)
      }
    }
  }
}

const CRC_32_MPEG2_MODEL = createModel({
  width: 32,
  poly: 0x04c11db7,
  init: 0xffffffff,
  refin: false,
  refout: false,
  xorout: 0x00000000
  /* name: 'CRC-32/MPEG-2', */
})

// Calculate a CRC check value on file bytes, typically firmware file bytes.
export const calcCrcMpeg2 = (fileBytes: Uint8Array, imageSize: number): number => {
  const fileBytesResized = new Uint8Array(
    fileBytes.buffer,
    fileBytes.byteOffset,
    Math.min(fileBytes.byteLength, imageSize)
  )
  if (fileBytesResized.length % 4 !== 0) {
    console.error('crc calculation error: image size not a multiple of 4')
    return -1
  }
  let crc = CRC_32_MPEG2_MODEL.create()
  let reversed = new Uint8Array(4)
  for (let i = 0; i < fileBytesResized.length; i += 4) {
    reversed[0] = fileBytesResized[i + 3]
    reversed[1] = fileBytesResized[i + 2]
    reversed[2] = fileBytesResized[i + 1]
    reversed[3] = fileBytesResized[i]
    crc.update(reversed)
  }
  return parseInt(crc.hex(), 16)
}

// CRC-32 matching STM32H7 Flash internal CRC module (per AN5507).
// Used by sacs-power-supply firmware
export const calcCrcStm32Flash = (fileBytes: Uint8Array, imageSize: number): number => {
  const length = Math.min(fileBytes.byteLength, imageSize)
  if (length % 4 !== 0) {
    console.error('crc calculation error: image size not a multiple of 4')
    return -1
  }

  const dv = new DataView(fileBytes.buffer, fileBytes.byteOffset, length)
  let crc = 0x00000000
  for (let i = 0; i < length; i += 4) {
    crc ^= dv.getUint32(i, true) // Little-endian
    for (let bit = 0; bit < 32; bit++) {
      if (crc & 0x80000000) {
        crc = ((crc << 1) ^ 0x04c11db7) >>> 0
      } else {
        crc = (crc << 1) >>> 0
      }
    }
    crc = (crc ^ 0x55555555) >>> 0 // Required per-word XOR (AN5507)
  }
  return crc
}
